import { Box } from "@material-ui/core";
import React, { useContext, useEffect, useState } from "react";
import { ProcessContext } from "../../../../contexts/ProcessContext";
import { fakeFormsDataProfessionalDetails } from "../../../../utils/fakeFormsData";
import { FormatDateMapper } from "../../../../utils/mappers/FormatDateMapper";
import { ProfessionalDetailsMapper } from "../../../../utils/mappers/ProfessionalDetailsMapper";
import { useStepHandler } from "../../../../utils/hooks/useStepHandler";
import BackButton from "../../../shared/components/BackButton";
import { assetTypes, STEP_GROUP_IDS } from "../../../shared/config";
import {
  employmentStatusTypesConfig,
  PROFESSIONAL_DATA_NAMES,
  PROFESSIONAL_POSITION_FILTER,
} from "./config";
import ProfessionalDataEmploymentForm from "./steps/ProfessionalDataEmploymentForm";
import ProfessionalDataSelfEmployedForm from "./steps/ProfessionalDataSelfEmployedForm";
import { removeNullKeysFromObject } from "../KycAutomatic/shared/constants/removeNullKeysFromObjects";
import ProfessionalDataCompanyForm from "./steps/ProfessionalDataCompanyForm";
import { THEME_NAMES } from "../../../../config/themes/config";
import { ConfigContext } from "../../../../contexts/ConfigContext";

const GET_NET_INCOME_MAX_ATTEMPTS = 8;
const GET_NET_INCOME_TIMEOUT = 1500;

export default function ProfessionalData(props) {
  const {
    state: {
      loan,
      income,
      lastActionsReferenceLastChange,
      lastActionsReference,
      participant,
    },
    dispatch,
  } = useContext(ProcessContext);
  const {
    state: { themeName },
  } = useContext(ConfigContext);

  const { disableOthers } = props;

  const STEP_GROUP_STEPS_ARRAY = [
    {
      id: PROFESSIONAL_DATA_NAMES.PROFESSIONAL_DATA_EMPLOYMENT_FORM,
      show: () => true,
    },
    {
      id: PROFESSIONAL_DATA_NAMES.PROFESSIONAL_DATA_ADDITIONAL_SELF_EMPLOYED_FORM,
      show: (allStepValues) =>
        allStepValues[PROFESSIONAL_DATA_NAMES.PROFESSIONAL_DATA_EMPLOYMENT_FORM]
          .employmentStatus ===
          employmentStatusTypesConfig.SELF_EMPLOYED.value &&
        themeName !== THEME_NAMES.caixabank &&
        loan.asset_reference[0].type === assetTypes.AUTO,
    },
    {
      id: PROFESSIONAL_DATA_NAMES.PROFESSIONAL_DATA_COMPANY_FORM,
      show: (allStepValues) =>
        PROFESSIONAL_POSITION_FILTER.find(
          (element) =>
            element ===
            allStepValues[
              PROFESSIONAL_DATA_NAMES.PROFESSIONAL_DATA_EMPLOYMENT_FORM
            ].employmentStatus
        ),
    },
  ];

  const CURRENT_STEP_GROUP = STEP_GROUP_IDS.ProfessionalData;

  const [state, setState] = useState({
    currentStepGroupStep:
      PROFESSIONAL_DATA_NAMES.PROFESSIONAL_DATA_EMPLOYMENT_FORM,
    formSubmitSuccess: false,
    netIncomeType: undefined,
    getNetIncomeAttempts: 0,
  });

  const { netIncomeType } = state;
  const onSubmitModule = (values) => {
    const allDataToSend = { ...values };
    const dehydratedData = ProfessionalDetailsMapper.dehydrate({
      ...allDataToSend,
      loanId: loan.id,
      id: participant.participantId,
    });
    if (
      values.employmentStatus ===
      employmentStatusTypesConfig.TEMPORARY_EMPLOYED.value
    ) {
      dehydratedData.professional_details.company_info.contract_date_end =
        FormatDateMapper.dehydrate(allDataToSend.contractDateEnd);
    }

    dispatch({
      type: "REQUEST_PATCH_SET_PROFESSIONAL_DETAILS_AND_SEQUENCE",
      payload: {
        op: "processPatchSetProfessionalDetails",
        ...removeNullKeysFromObject(dehydratedData),
      },
    });
    dispatch({
      type: "SET_FLOW_LOADING",
      payload: {
        show: true,
      },
    });
  };

  const currentModuleStepHandler = useStepHandler(
    onSubmitModule,
    fakeFormsDataProfessionalDetails
  );

  const {
    manageGroup,
    submitStep,
    onBack,
    backButtonVisible,
    getStepData,
    allStepValues,
  } = currentModuleStepHandler;

  const stepGroupStepToShow = manageGroup(
    CURRENT_STEP_GROUP,
    STEP_GROUP_STEPS_ARRAY
  );
  const { formSubmitSuccess, getNetIncomeAttempts } = state;

  const employmentStatus =
    allStepValues[PROFESSIONAL_DATA_NAMES.PROFESSIONAL_DATA_EMPLOYMENT_FORM]
      ?.employmentStatus;

  useEffect(() => {
    if (loan) {
      dispatch({
        type: "REQUEST_GET_LOAN",
        payload: loan.id,
      });
      getNetIncomeByLoanId();
    }
  }, []);

  useEffect(() => {
    const isActionTypeInLastActionsReference = (actionType) =>
      lastActionsReference?.some((element) => element.type === actionType);

    if (formSubmitSuccess) {
      if (isActionTypeInLastActionsReference("REQUEST_GET_LOAN_FULFILLED")) {
        dispatch({
          type: "SET_FLOW_ITEM_OK",
        });
      }
    }
    if (
      isActionTypeInLastActionsReference(
        "REQUEST_PATCH_SET_PROFESSIONAL_DETAILS_AND_SEQUENCE_FULFILLED"
      )
    ) {
      setFormSubmitSuccess();
    }
    if (!formSubmitSuccess) {
      if (
        isActionTypeInLastActionsReference(
          "REQUEST_GET_DECLARED_INCOME_FULFILLED"
        )
      ) {
        setState((s) => ({
          ...s,
          netIncomeType: income?.net_income?.net_income_type,
        }));
      }
      if (
        isActionTypeInLastActionsReference(
          "REQUEST_GET_DECLARED_INCOME_REJECTED"
        )
      ) {
        setState((s) => ({
          ...s,
          getNetIncomeAttempts: getNetIncomeAttempts + 1,
        }));
        if (getNetIncomeAttempts <= GET_NET_INCOME_MAX_ATTEMPTS) {
          setTimeout(() => getNetIncomeByLoanId(), GET_NET_INCOME_TIMEOUT);
        }
      }
    }
  }, [formSubmitSuccess, lastActionsReferenceLastChange]);

  const getNetIncomeByLoanId = () => {
    dispatch({
      type: "REQUEST_GET_DECLARED_INCOME",
      payload: {
        loanId: loan.id,
        onboardingDetailsId: participant.onboardingDetails.id,
      },
    });
  };

  const setFormSubmitSuccess = () => {
    setState((s) => ({
      ...s,
      formSubmitSuccess: true,
    }));
  };

  return (
    <>
      <Box className="appear-anim" style={{ position: "absolute" }}>
        <BackButton onBack={onBack} show={backButtonVisible} />
      </Box>
      {stepGroupStepToShow ===
        PROFESSIONAL_DATA_NAMES.PROFESSIONAL_DATA_EMPLOYMENT_FORM && (
        <ProfessionalDataEmploymentForm
          netIncomeType={netIncomeType}
          onSubmitForm={submitStep}
          data={getStepData()}
          disableOthers={disableOthers}
        />
      )}
      {stepGroupStepToShow ===
        PROFESSIONAL_DATA_NAMES.PROFESSIONAL_DATA_ADDITIONAL_SELF_EMPLOYED_FORM && (
        <ProfessionalDataSelfEmployedForm
          onSubmitForm={submitStep}
          data={getStepData()}
        />
      )}
      {stepGroupStepToShow ===
        PROFESSIONAL_DATA_NAMES.PROFESSIONAL_DATA_COMPANY_FORM && (
        <ProfessionalDataCompanyForm
          onSubmitForm={submitStep}
          data={getStepData()}
          employmentStatus={employmentStatus}
        />
      )}
    </>
  );
}
